<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue'
import { userModule } from '@/store/user/module'
import UIKit from '@pocketprep/ui-kit'

const PocketButton = UIKit.Button
const ModalContainer = UIKit.ModalContainer
const Modal = UIKit.Modal

withDefaults(defineProps<{
    numAnswers?: number
    quizLength?: number
}>(), {
    numAnswers: 0,
    quizLength: 1,
})

const emit = defineEmits<{
    close: []
    continue: []
    submit: []
}>()

const titleRef = ref<HTMLElement>()

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

onMounted(() => {
    nextTick(() => {
        titleRef.value?.focus()
    })
})

const emitClose = () => {
    emit('close')
}

const emitContinue = () => {
    emit('continue')
}

const emitSubmit = () => {
    emit('submit')
}
</script>

<template>
    <Portal to="modal">
        <ModalContainer
            key="submitExamModal"
            :show-close-button="true"
            :is-dark-mode="isDarkMode"
            @close="emitClose"
        >
            <template #modal>
                <Modal
                    :show-close-button="true"
                    class="submit-exam__modal"
                    :is-dark-mode="isDarkMode"
                    @close="emitClose"
                >
                    <div class="submit-exam">
                        <h1
                            ref="titleRef"
                            class="submit-exam__title"
                            tabindex="-1"
                        >
                            Submit Mock Exam?
                        </h1>
                        <div
                            v-if="numAnswers >= quizLength"
                            v-dark
                            class="submit-exam__text"
                        >
                            You've answered {{ numAnswers }} of {{ quizLength }} questions.
                            Submit to score your exam.
                        </div>
                        <div
                            v-else
                            v-dark
                            class="submit-exam__text"
                        >
                            You've answered {{ numAnswers }} of {{ quizLength }} questions.
                            If you submit, unanswered questions will be marked incorrect.
                        </div>
                        <div class="submit-exam__actions">
                            <PocketButton
                                type="secondary"
                                class="submit-exam__continue-btn"
                                :is-dark-mode="isDarkMode"
                                @click="emitContinue"
                            >
                                Cancel
                            </PocketButton>
                            <PocketButton
                                class="submit-exam__submit-btn"
                                :is-dark-mode="isDarkMode"
                                @click="emitSubmit"
                            >
                                Submit Exam
                            </PocketButton>
                        </div>
                    </div>
                </Modal>
            </template>
        </ModalContainer>
    </Portal>
</template>

<style lang="scss" scoped>
.submit-exam {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 35px 26px 26px 27px;
    width: 384px;
    margin: 0 auto;

    @include breakpoint(black-bear) {
        max-width: 340px;
        max-height: 226px;
        margin: 0 auto;
        width: 100;
    }

    &__title {
        outline: none;
        font-weight: 600;
        font-size: 18px;
        line-height: 22px;
        margin: 0 0 12px 0;
    }

    &__text {
        width: 294px;
        color: $slate-03;
        font-size: 15px;
        line-height: 22px;
        text-align: center;
        margin-bottom: 29px;

        &--dark {
            color: $white;
        }
    }

    &__continue-btn {
        margin-right: 12px;
    }

    &__modal {
        left: 50%;
        position: absolute;
        transform: translateX(-50%);
        top: 94px;
    }

    &__actions {
        display: flex;
        justify-content: center;
        align-items: center;

        .uikit-btn--primary {
            padding: 9px 15px 8px 15px;
        }

        .uikit-btn--secondary {
            margin: 0 4px;
            padding: 9px 20px 8px 21px;
        }
    }
}

</style>
